import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { registerPlan, clearAllMessages } from '../redux/actions/planActions';
import { AppDispatch, RootState } from '../redux/store';
import styles from './PlanRegistration.module.css';
import ReturnToDashboard from './ReturnToDashboard';
import { AuthContext } from '../App';

const PlanRegistration: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const registrationMessage = useSelector((state: RootState) => state.plan.registrationMessage);
  const registrationError = useSelector((state: RootState) => state.plan.error);
  const { token: contextToken } = useContext(AuthContext);
  const [token, setToken] = useState(contextToken || localStorage.getItem('authToken'));

  useEffect(() => {
    if (!token) {
      const storedToken = localStorage.getItem('authToken');
      if (storedToken) {
        setToken(storedToken);
      }
    }
  }, [token]);

  const initialFormData = {
    couponCode: '',
    brokerID: '',
    brokerName: '',
    employerName: '',
    planName: '',
    planDescription: '',
    price: '',
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showMessage, setShowMessage] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.couponCode) newErrors.couponCode = 'Coupon Code is required';
    if (!formData.brokerID) newErrors.brokerID = 'Broker ID is required';
    if (!formData.brokerName) newErrors.brokerName = 'Broker Name is required';
    if (!formData.employerName) newErrors.employerName = 'Employer Name is required';
    if (!formData.planName) newErrors.planName = 'Plan Name is required';
    if (formData.price && isNaN(Number(formData.price))) newErrors.price = 'Price must be a number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const planData = {
        brokerID: formData.brokerID,
        brokerName: formData.brokerName,
        employerName: formData.employerName,
        planName: formData.planName,
        planCode: formData.couponCode,
        planDescription: formData.planDescription,
        price: formData.price
      };
      dispatch(registerPlan(planData));
    }
  };

  const handleClear = () => {
    setFormData(initialFormData);
    setErrors({});
  };

  // Clear messages when component mounts or unmounts
  useEffect(() => {
    dispatch(clearAllMessages());
    return () => {
      dispatch(clearAllMessages());
    };
  }, [dispatch]);

  // Clear messages when location changes (i.e., when navigating)
  useEffect(() => {
    dispatch(clearAllMessages());
  }, [location, dispatch]);

  // Reset form data after successful registration
  useEffect(() => {
    if (registrationMessage) {
      setFormData(initialFormData); // Reset form data
    }
  }, [registrationMessage]);

  useEffect(() => {
    if (registrationMessage || registrationError) {
      setShowMessage(true);
      const timer = setTimeout(() => {
        setShowMessage(false);
        dispatch(clearAllMessages());
      }, 5000); // Hide message after 5 seconds
      return () => clearTimeout(timer);
    }
  }, [registrationMessage, registrationError, dispatch]);

  return (
    <div className={styles.planRegistration}>
      <ReturnToDashboard />
      <div className={styles.container}>
        <header className={styles.header}>
          <h1>GMR Plan Registration</h1>
        </header>
        <div className={styles.formContainer}>
          <h2>Register Plan codes for Brokers And Employers</h2>
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="couponCode">Coupon Code*</label>
                <input
                  type="text"
                  id="couponCode"
                  name="couponCode"
                  value={formData.couponCode}
                  onChange={handleChange}
                  className={errors.couponCode ? styles.errorInput : ''}
                />
                {errors.couponCode && <span className={styles.errorMessage}>{errors.couponCode}</span>}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="brokerID">Broker ID*</label>
                <input
                  type="text"
                  id="brokerID"
                  name="brokerID"
                  value={formData.brokerID}
                  onChange={handleChange}
                  className={errors.brokerID ? styles.errorInput : ''}
                />
                {errors.brokerID && <span className={styles.errorMessage}>{errors.brokerID}</span>}
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="brokerName">Broker Name*</label>
                <input
                  type="text"
                  id="brokerName"
                  name="brokerName"
                  value={formData.brokerName}
                  onChange={handleChange}
                  className={errors.brokerName ? styles.errorInput : ''}
                />
                {errors.brokerName && <span className={styles.errorMessage}>{errors.brokerName}</span>}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="employerName">Employer Name*</label>
                <input
                  type="text"
                  id="employerName"
                  name="employerName"
                  value={formData.employerName}
                  onChange={handleChange}
                  className={errors.employerName ? styles.errorInput : ''}
                />
                {errors.employerName && <span className={styles.errorMessage}>{errors.employerName}</span>}
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="planName">Plan Name*</label>
                <input
                  type="text"
                  id="planName"
                  name="planName"
                  value={formData.planName}
                  onChange={handleChange}
                  className={errors.planName ? styles.errorInput : ''}
                />
                {errors.planName && <span className={styles.errorMessage}>{errors.planName}</span>}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="price">Price</label>
                <input
                  type="text"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  className={errors.price ? styles.errorInput : ''}
                />
                {errors.price && <span className={styles.errorMessage}>{errors.price}</span>}
              </div>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="planDescription">Plan Description</label>
              <textarea
                id="planDescription"
                name="planDescription"
                value={formData.planDescription}
                onChange={handleChange}
              />
            </div>
            <div className={styles.formActions}>
              <div className={styles.buttonGroup}>
                <button type="submit" className={styles.submitButton}>Register</button>
                <button type="button" className={styles.clearButton} onClick={handleClear}>Clear</button>
              </div>
              {showMessage && (
                <div className={`${styles.messageContainer} ${registrationError ? styles.errorMessage : styles.successMessage}`}>
                  <p>
                    {registrationError || registrationMessage}
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PlanRegistration;
